<template>
  <div v-if="equipment" class="space-y-3">
    <!-- Main Image -->
    <div class="relative group rounded-2xl overflow-hidden bg-theme-bg2 border border-theme-border">
      <div class="relative aspect-[4/3]">
        <img
          v-if="currentImage"
          :src="currentImage"
          :alt="equipment.name"
          class="w-full h-full object-cover transition-transform duration-500"
          @mousemove="zoomImage"
          @mouseleave="resetZoom"
          ref="mainImage"
          loading="lazy"
        />
        <div v-else class="w-full h-full flex flex-col items-center justify-center">
          <i class="pi pi-image text-5xl text-theme-muted"></i>
          <span class="mt-2 text-theme-muted text-sm">No images available</span>
        </div>

        <!-- Zoom overlay -->
        <div
          v-if="zoomed && currentImage"
          class="zoomed-image pointer-events-none"
          :style="{ backgroundImage: `url(${currentImage})`, backgroundPosition: zoomPosition }"
        ></div>

        <!-- Nav arrows -->
        <template v-if="images.length > 1">
          <button @click="prevImage"
            class="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm">
            <i class="pi pi-chevron-left text-sm"></i>
          </button>
          <button @click="nextImage"
            class="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm">
            <i class="pi pi-chevron-right text-sm"></i>
          </button>
        </template>

        <!-- Image counter pill -->
        <div v-if="images.length > 1"
          class="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
          {{ currentIndex + 1 }} / {{ images.length }}
        </div>
      </div>
    </div>

    <!-- Thumbnail strip -->
    <div v-if="images.length > 1" class="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
      <button
        v-for="(image, index) in images"
        :key="`thumb-${index}`"
        @click="setCurrentImage(index)"
        class="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all duration-200"
        :class="currentIndex === index
          ? 'border-primary-600 ring-2 ring-primary-600/30 shadow-md'
          : 'border-transparent hover:border-theme-border opacity-70 hover:opacity-100'"
      >
        <img
          v-if="image.image_url"
          :src="image.image_url"
          :alt="`Thumbnail ${index + 1}`"
          class="w-full h-full object-cover"
          loading="lazy"
          @error="handleImageError(index)"
        />
        <div v-else class="w-full h-full bg-theme-bg2 flex items-center justify-center">
          <i class="pi pi-image text-sm text-theme-muted"></i>
        </div>
      </button>
    </div>
  </div>

  <!-- Loading state -->
  <div v-else-if="loading" class="space-y-3">
    <div class="aspect-[4/3] bg-theme-bg2 rounded-2xl animate-pulse"></div>
    <div class="flex gap-2">
      <div v-for="i in 4" :key="i" class="w-20 h-20 bg-theme-bg2 rounded-xl animate-pulse"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import useNotifications from '@/store/notification';

const equipment = ref(null);
const currentIndex = ref(0);
const images = ref([]);
const zoomed = ref(false);
const zoomPosition = ref('0%');
const loading = ref(true);
const error = ref(null);

const api_base_url = import.meta.env.VITE_API_BASE_URL;
const { showNotification } = useNotifications();
const route = useRoute();
const equipmentId = route.params.id;

const currentImage = computed(() => images.value[currentIndex.value]?.image_url || null);

const fetchEquipmentData = async () => {
  if (!equipmentId) {
    error.value = 'No item ID provided';
    loading.value = false;
    return;
  }

  try {
    const response = await axios.get(
      `${api_base_url}/api/equipments/${equipmentId}`,
      { timeout: 5000 }
    );

    equipment.value = response.data;
    if (response.data?.images?.length > 0) {
      images.value = response.data.images;
      currentIndex.value = 0;
    } else {
      images.value = [];
    }
  } catch (err) {
    error.value = err;
    showNotification(
      'Error Loading item',
      err.response?.data?.message || err.message || 'Failed to load item details',
      'error'
    );
  } finally {
    loading.value = false;
  }
};

const setCurrentImage = (index) => {
  currentIndex.value = index;
};

const prevImage = () => {
  if (images.value.length <= 1) return;
  currentIndex.value = (currentIndex.value - 1 + images.value.length) % images.value.length;
};

const nextImage = () => {
  if (images.value.length <= 1) return;
  currentIndex.value = (currentIndex.value + 1) % images.value.length;
};

const zoomImage = (event) => {
  if (!event.target) return;
  const { offsetX, offsetY } = event;
  const { clientWidth, clientHeight } = event.target;
  const xPercent = (offsetX / clientWidth) * 100;
  const yPercent = (offsetY / clientHeight) * 100;
  zoomPosition.value = `${xPercent}% ${yPercent}%`;
  zoomed.value = true;
};

const resetZoom = () => {
  zoomed.value = false;
};

const handleImageError = (index) => {
  if (images.value[index]) {
    images.value[index].image_url = null;
  }
};

onMounted(() => {
  fetchEquipmentData();
});
</script>

<style scoped>
.zoomed-image {
  position: absolute;
  inset: 0;
  background-size: 200%;
  background-repeat: no-repeat;
  transition: opacity 0.2s ease;
  opacity: 1;
  z-index: 10;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>
